/* Soundmouse theme — module animations + interactions
   Staged headline reveals, scroll-triggered entrances, stat count-ups,
   logo marquee (Swiper), click-to-play video embeds. */

import Swiper from "swiper";
import { Autoplay } from "swiper/modules";

(function () {
  "use strict";

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- split headlines into words for staged reveal ---------- */

  function splitWords(node) {
    var children = Array.prototype.slice.call(node.childNodes);
    children.forEach(function (child) {
      if (child.nodeType === Node.TEXT_NODE) {
        var frag = document.createDocumentFragment();
        child.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) { return; }
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(" "));
          } else {
            var span = document.createElement("span");
            span.className = "word";
            span.textContent = part;
            frag.appendChild(span);
          }
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        /* recurse into wrapping elements (e.g. the <p> a WYSIWYG field's
           wpautop adds) so words nested inside them still get split; <br>
           and other childless elements just no-op here and stay in place */
        splitWords(child);
      }
    });
  }

  function staggerWords(el) {
    el.querySelectorAll(".word").forEach(function (w, i) {
      w.style.transitionDelay = i * 90 + "ms";
    });
    el.classList.add("words-in");
  }

  if (!reducedMotion) {
    document.querySelectorAll("[data-split-reveal], [data-split-reveal-scroll]").forEach(splitWords);

    /* hero headline: reveal on load */
    document.querySelectorAll("[data-split-reveal]").forEach(function (el) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { staggerWords(el); });
      });
    });
  }

  /* ---------- scroll-triggered reveals + count-ups ---------- */

  document.querySelectorAll(".reveal").forEach(function (el) {
    var d = el.getAttribute("data-reveal-delay");
    if (d) { el.style.setProperty("--reveal-delay", d + "ms"); }
  });

  /* odometer roll (Figma "rolling-counter"): each digit becomes a vertical
     reel of 0-9 cycles that spins to the target with a motion blur */
  function countUp(el) {
    var target = String(el.getAttribute("data-count"));
    el.textContent = "";
    target.split("").forEach(function (ch, i) {
      if (!/[0-9]/.test(ch)) {
        el.appendChild(document.createTextNode(ch));
        return;
      }
      var steps = (i + 2) * 10 + parseInt(ch, 10); /* later digits spin further */
      var col = document.createElement("span");
      col.className = "digit";
      var reel = document.createElement("span");
      reel.className = "digit-reel";
      for (var n = 0; n <= steps; n++) {
        var d = document.createElement("span");
        d.textContent = n % 10;
        reel.appendChild(d);
      }
      reel.style.transition =
        "transform " + (1200 + i * 250) + "ms cubic-bezier(0.23, 1, 0.32, 1)";
      col.appendChild(reel);
      el.appendChild(col);

      reel.classList.add("rolling-now");
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          reel.style.transform = "translateY(-" + steps + "em)";
        });
      });
      reel.addEventListener("transitionend", function () {
        reel.classList.remove("rolling-now");
      }, { once: true });
    });
  }

  if ("IntersectionObserver" in window && !reducedMotion) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) { return; }
        var el = entry.target;
        if (el.hasAttribute("data-split-reveal-scroll")) {
          staggerWords(el);
        } else {
          el.classList.add("is-visible");
          /* stats stagger their fade-in via data-reveal-delay (0/150/300ms);
             the count-up should start on that same beat instead of all
             three rolling at once, so they finish in a 1, 2, 3 sequence */
          var delay = parseInt(el.getAttribute("data-reveal-delay"), 10) || 0;
          el.querySelectorAll(".count").forEach(function (count) {
            setTimeout(function () { countUp(count); }, delay);
          });
        }
        io.unobserve(el);
      });
    }, { threshold: 0.25 });

    document.querySelectorAll(".reveal, [data-split-reveal-scroll]").forEach(function (el) {
      io.observe(el);
    });
  } else {
    document.querySelectorAll(".reveal").forEach(function (el) {
      el.classList.add("is-visible");
    });
    document.querySelectorAll(".count").forEach(function (el) {
      el.textContent = el.getAttribute("data-count");
    });
  }

  /* ---------- scroll parallax (mesh background + media/text photo) ----------
     Drives CSS vars consumed by _container.scss (--plx-bg on the mesh
     section's ::before) and _media-text.scss (--plx on the photo). Progress
     runs -1..1 as the watched section crosses the viewport. */

  if (!reducedMotion) {
    var plx = [];
    /* background travel distance, doubled from the original 90 each time
       (90 -> 210 -> 420 -> 840) to read as clearly moving against the
       foreground */
    document.querySelectorAll(".module-container--mesh").forEach(function (el) {
      plx.push({ watch: el, set: el, prop: "--plx-bg", speed: 840 });
    });
    /* photo + card drift upward as the section scrolls up the viewport
       (and back down when scrolling up); two speeds for a little depth.
       Speeds are 15% down from 100/60 (Music Recognition cards) */
    document.querySelectorAll(".module-media-text").forEach(function (el) {
      var photo = el.querySelector(".mt-photo");
      var card = el.querySelector(".mt-card");
      if (photo) { plx.push({ watch: el, set: photo, prop: "--plx", speed: 85 }); }
      if (card) { plx.push({ watch: el, set: card, prop: "--plx-card", speed: 51 }); }
    });
    if (plx.length) {
      var plxQueued = false;
      var updateParallax = function () {
        plxQueued = false;
        var vh = window.innerHeight;
        plx.forEach(function (p) {
          var r = p.watch.getBoundingClientRect();
          if (r.bottom < -200 || r.top > vh + 200) { return; }
          var progress = ((r.top + r.height / 2 - vh / 2) / (vh + r.height)) * 2;
          p.set.style.setProperty(p.prop, (progress * p.speed).toFixed(1) + "px");
        });
      };
      var queueParallax = function () {
        if (!plxQueued) {
          plxQueued = true;
          requestAnimationFrame(updateParallax);
        }
      };
      window.addEventListener("scroll", queueParallax, { passive: true });
      window.addEventListener("resize", queueParallax, { passive: true });
      updateParallax();
    }
  }

  /* ---------- infinite logo marquee (Swiper) ----------
     Autoplay + drag on mobile and desktop. Desktop only activates when
     there are enough logos (6+); fewer stay centered/static. Always loops.
     Kept hidden until init finishes so Safari doesn't flash clones/transforms. */

  document.querySelectorAll(".module-logo-marquee").forEach(function (marquee) {
    var el = marquee.querySelector(".logo-marquee");
    var track = marquee.querySelector(".logo-track");
    if (!el || !track) { return; }

    var DESKTOP_MIN_LOGOS = 6;
    var desktopMq = window.matchMedia("(min-width: 769px)");
    var originalSlides = Array.prototype.slice.call(track.querySelectorAll(".swiper-slide"));
    if (!originalSlides.length) { return; }

    var swiperInstance = null;
    var mode = null; // "scroll" | "static"

    function isDesktop() {
      return desktopMq.matches;
    }

    function shouldScroll() {
      return !isDesktop() || originalSlides.length >= DESKTOP_MIN_LOGOS;
    }

    function resetSlides() {
      var frag = document.createDocumentFragment();
      originalSlides.forEach(function (slide) {
        frag.appendChild(slide.cloneNode(true));
      });
      track.innerHTML = "";
      track.appendChild(frag);
    }

    function ensureLoopSlides() {
      var guard = 0;
      while (track.scrollWidth < el.clientWidth * 2 && guard < 8) {
        var frag = document.createDocumentFragment();
        originalSlides.forEach(function (slide) {
          frag.appendChild(slide.cloneNode(true));
        });
        track.appendChild(frag);
        guard += 1;
      }
    }

    function reveal() {
      /* double rAF so Safari paints the settled transform before fade-in */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.classList.add("is-ready");
        });
      });
    }

    function destroySwiper() {
      if (swiperInstance) {
        swiperInstance.destroy(true, true);
        swiperInstance = null;
      }
    }

    function enterStatic() {
      if (mode === "static") {
        reveal();
        return;
      }
      el.classList.remove("is-ready");
      destroySwiper();
      resetSlides();
      el.classList.add("logo-marquee--static");
      mode = "static";
      reveal();
    }

    function enterScrolling() {
      if (mode === "scroll" && swiperInstance) {
        swiperInstance.update();
        reveal();
        return;
      }
      el.classList.remove("is-ready");
      destroySwiper();
      resetSlides();
      el.classList.remove("logo-marquee--static");
      ensureLoopSlides();

      swiperInstance = new Swiper(el, {
        modules: [Autoplay],
        slidesPerView: "auto",
        spaceBetween: 16,
        loop: true,
        loopAdditionalSlides: originalSlides.length,
        speed: 5000,
        allowTouchMove: true,
        grabCursor: true,
        watchSlidesProgress: true,
        /* avoid Safari init flash from observer/mutation reflows */
        observer: false,
        observeParents: false,
        autoplay: reducedMotion
          ? false
          : {
              delay: 0,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            },
        on: {
          init: function () {
            mode = "scroll";
            reveal();
          },
        },
      });
    }

    function refresh() {
      if (shouldScroll()) {
        enterScrolling();
      } else {
        enterStatic();
      }
    }

    var imgs = track.querySelectorAll("img");
    var remaining = imgs.length;
    var ready = false;

    function settled() {
      remaining -= 1;
      if (remaining <= 0 && !ready) {
        ready = true;
        refresh();
      }
    }

    if (!remaining) {
      ready = true;
      refresh();
    } else {
      imgs.forEach(function (img) {
        if (img.complete) {
          settled();
        } else {
          img.addEventListener("load", settled);
          img.addEventListener("error", settled);
        }
      });
    }

    var resizeTimer = null;
    window.addEventListener(
      "resize",
      function () {
        if (!ready) { return; }
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(refresh, 150);
      },
      { passive: true }
    );
  });

  /* ---------- video: muted background autoplay, click restarts with sound ----------
     On load the frame plays a muted, looping, chrome-less embed behind the
     play button; clicking rebuilds the embed unmuted from the beginning. */

  function embedSrc(url, background) {
    var yt = url.match(/(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (yt) {
      return "https://www.youtube.com/embed/" + yt[1] + "?" + (background
        ? "autoplay=1&mute=1&controls=0&loop=1&playlist=" + yt[1] + "&playsinline=1&rel=0&modestbranding=1&enablejsapi=1"
        : "autoplay=1&rel=0");
    }
    var vimeo = url.match(/vimeo\.com\/([0-9]+)/);
    if (vimeo) {
      return "https://player.vimeo.com/video/" + vimeo[1] + "?" + (background
        ? "background=1&autoplay=1&muted=1&loop=1"
        : "autoplay=1");
    }
    return false;
  }

  function buildVideoIframe(src, interactive) {
    var iframe = document.createElement("iframe");
    iframe.src = src;
    iframe.title = "Video player";
    iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
    iframe.allowFullscreen = true;
    if (!interactive) {
      iframe.className = "video-bg";
      iframe.setAttribute("tabindex", "-1");
      iframe.setAttribute("aria-hidden", "true");
    }
    return iframe;
  }

  document.querySelectorAll("[data-video-embed]").forEach(function (frame) {
    var url = frame.getAttribute("data-video-embed");

    if (!reducedMotion) {
      var bgSrc = embedSrc(url, true);
      if (bgSrc) {
        var bgIframe = buildVideoIframe(bgSrc, false);
        bgIframe.addEventListener("load", function () {
          frame.classList.add("video-live");
          /* YouTube force-enables its caption layer on muted autoplay,
             doubling the captions burned into the video — unload it via
             the player's command channel (retry while it boots) */
          var ccTries = 0;
          var killCC = setInterval(function () {
            if (++ccTries > 12) { clearInterval(killCC); return; }
            ["captions", "cc"].forEach(function (m) {
              bgIframe.contentWindow.postMessage(JSON.stringify({ event: "command", func: "unloadModule", args: [m] }), "*");
            });
          }, 500);
        });
        frame.insertBefore(bgIframe, frame.querySelector(".play-btn"));
      }
    }

    frame.addEventListener("click", function () {
      var src = embedSrc(url, false);
      if (!src) { return; }
      frame.innerHTML = "";
      frame.appendChild(buildVideoIframe(src, true));
      frame.style.cursor = "default";
    }, { once: true });
  });

  /* ---------- self-hosted video: same behavior, zero player chrome ---------- */

  document.querySelectorAll("[data-video-file]").forEach(function (frame) {
    var video = document.createElement("video");
    video.src = frame.getAttribute("data-video-file");
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.className = "video-bg";

    frame.insertBefore(video, frame.querySelector(".play-btn"));

    if (!reducedMotion) {
      frame.classList.add("video-live"); /* no title overlay to mask */
      video.autoplay = true;
      video.play().catch(function () {});
    }

    frame.addEventListener("click", function () {
      video.classList.remove("video-bg");
      video.muted = false;
      video.loop = false;
      video.controls = true;
      video.currentTime = 0;
      var btn = frame.querySelector(".play-btn");
      if (btn) { btn.remove(); }
      frame.classList.add("video-live");
      frame.style.cursor = "default";
      video.play().catch(function () {});
    }, { once: true });
  });
})();
